import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import NavbarMob from "../components/NavbarMob";
import Navbar from "../components/Navbar";
import Navbar2 from "../components/Navbar2";
import Navbar2Mob from "../components/Navbar2Mob";
import Footer from "../components/Footer";

function PlansManagement() {
  const [plans, setPlans] = useState([]);
  const [planOrder, setPlanOrder] = useState([]);
  const [newPlan, setNewPlan] = useState({
    planName: "",
    planCode: "",
    durationDays: "",
    description: "",
  });
  const [editingPlan, setEditingPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  /* -------------------------------------------------- FETCH -------------------------------------------------- */
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/plans`);
      const data = await response.json();

      if (data.success) {
        const normalized = data.plans.map(p => ({
          PlanID: p.PlanID,
          PlanName: p.PlanName || "",
          PlanCode: p.PlanCode || "",
          DurationDays: p.DurationDays != null ? String(p.DurationDays) : "",
          Description: p.Description || "",
          displayOrder: p.displayOrder ?? 999,
        }));

        setPlans(normalized);

        const orderedIds = normalized
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map(p => p.PlanID);

        setPlanOrder(orderedIds);
      } else {
        setError(data.message || "Failed to fetch plans");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch plans");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------- REORDER -------------------------------------------------- */
  const movePlan = async (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === planOrder.length - 1)
    )
      return;

    const newOrder = [...planOrder];
    const target = direction === "up" ? index - 1 : index + 1;
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    setPlanOrder(newOrder);

    try {
      const res = await fetch(`${apiBaseUrl}/plans/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: newOrder }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || "Failed to save order");
        setPlanOrder(planOrder);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save order");
      setPlanOrder(planOrder);
    }
  };

  /* -------------------------------------------------- INPUT -------------------------------------------------- */
  const handleInputChange = e => {
    const { name, value } = e.target;
    const map = {
      planName: "PlanName",
      planCode: "PlanCode",
      durationDays: "DurationDays",
      description: "Description",
    };
    const dbKey = map[name];
    if (!dbKey) return;

    if (editingPlan) {
      setEditingPlan(prev => ({ ...prev, [dbKey]: value }));
    } else {
      setNewPlan(prev => ({ ...prev, [name]: value }));
    }
  };

  /* -------------------------------------------------- SUBMIT -------------------------------------------------- */
  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // ---- Build payload exactly as backend expects ----
      const payload = editingPlan
        ? {
            planName: editingPlan.PlanName,
            planCode: editingPlan.PlanCode,
            durationDays: Number(editingPlan.DurationDays), // <-- number
            description: editingPlan.Description,
          }
        : {
            planName: newPlan.planName,
            planCode: newPlan.planCode,
            durationDays: Number(newPlan.durationDays),
            description: newPlan.description,
          };

      const url = editingPlan
        ? `${apiBaseUrl}/plans/${editingPlan.PlanID}`
        : `${apiBaseUrl}/plans`;
      const method = editingPlan ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(editingPlan ? "Plan updated!" : "Plan created!");
        setNewPlan({ planName: "", planCode: "", durationDays: "", description: "" });
        setEditingPlan(null);
        fetchPlans();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.message || (editingPlan ? "Update failed" : "Create failed"));
      }
    } catch (err) {
      console.error(err);
      setError(editingPlan ? "Update failed" : "Create failed");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------- EDIT / CANCEL -------------------------------------------------- */
  const handleEdit = plan => {
    // console.log("Editing plan:", plan);
    setEditingPlan({ ...plan }); // clone to avoid mutation
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setError(null);
  };

  /* -------------------------------------------------- DELETE -------------------------------------------------- */
  const handleDelete = async planId => {
    if (!window.confirm("Delete this plan? This cannot be undone.")) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/plans/${planId}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setSuccessMessage("Plan deleted!");
        fetchPlans();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      setError("Delete failed");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------- RENDER -------------------------------------------------- */
  const orderedPlans = planOrder
    .map(id => plans.find(p => p.PlanID === id))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <NavbarMob /> : <Navbar />}
      <div className="md:flex hidden"><Navbar2 /></div>
      <div className="md:hidden flex flex-col"><Navbar2Mob /></div>

      <div className="max-w-6xl mx-auto py-12 px-4">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-800">Subscription Plans Management</h2>
          <p className="text-gray-600 mt-2">Create, edit, delete, and reorder subscription plans</p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border-l-4 border-green-500 text-green-700 rounded">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* ---------- FORM ---------- */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div
            className={`bg-gradient-to-r px-6 py-4 ${
              editingPlan ? "from-yellow-600 to-yellow-500" : "from-blue-600 to-blue-500"
            }`}
          >
            <h3 className="text-xl font-semibold text-white">
              {editingPlan ? `Edit Plan: ${editingPlan.PlanName}` : "Create New Plan"}
            </h3>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                  <input
                    type="text"
                    name="planName"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editingPlan ? editingPlan.PlanName || "" : newPlan.planName}
                    onChange={handleInputChange}
                    placeholder="e.g., Premium Plan"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Code</label>
                  <input
                    type="text"
                    name="planCode"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editingPlan ? editingPlan.PlanCode || "" : newPlan.planCode}
                    onChange={handleInputChange}
                    placeholder="e.g., PREMIUM_30"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                  <input
                    type="number"
                    name="durationDays"
                    min="1"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editingPlan ? editingPlan.DurationDays || "" : newPlan.durationDays}
                    onChange={handleInputChange}
                    placeholder="e.g., 30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={editingPlan ? editingPlan.Description || "" : newPlan.description}
                  onChange={handleInputChange}
                  placeholder="Describe the plan features..."
                />
              </div>

              <div className="flex justify-end space-x-4">
                {editingPlan && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {isLoading ? "Saving..." : editingPlan ? "Update Plan" : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ---------- TABLE ---------- */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-gray-700 to-gray-600 px-6 py-4">
            <h3 className="text-xl font-semibold text-white">Existing Plans</h3>
          </div>

          <div className="p-6">
            {isLoading && !plans.length ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : orderedPlans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No plans found. Create your first plan above!
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Name
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderedPlans.map((plan, idx) => {
                      const isFirst = idx === 0;
                      const isLast = idx === orderedPlans.length - 1;

                      return (
                        <tr key={plan.PlanID} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          {/* ORDER BUTTONS */}
                          <td className="px-4 py-4 text-center">
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => movePlan(idx, "up")}
                                disabled={isFirst || isLoading}
                                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition"
                                title="Move up"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => movePlan(idx, "down")}
                                disabled={isLast || isLoading}
                                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition"
                                title="Move down"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{plan.PlanName}</div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{plan.PlanCode}</span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{plan.DurationDays} days</div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium space-x-3">
                            <button
                              onClick={() => handleEdit(plan)}
                              className="text-indigo-600 hover:text-indigo-900 transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(plan.PlanID)}
                              className="text-red-600 hover:text-red-900 transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-12"><Footer /></div>
    </div>
  );
}

export default PlansManagement;